import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useReducer, useEffect, useLayoutEffect, useState, useImperativeHandle, forwardRef, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { annotationReducer, createInitialAnnotationState } from './annotationReducer';
import { SvgCanvas } from './components/SvgCanvas';
import { Toolbar } from './components/Toolbar';
import { SelectionOutline } from './components/SelectionOutline';
import { useDrawing } from './hooks/useDrawing';
import { useContainerSize } from './hooks/useContainerSize';
import { useShapeMove } from './hooks/useShapeMove';
import { useShapeResize } from './hooks/useShapeResize';
import { useShapeRotate } from './hooks/useShapeRotate';
import { useEraser, ERASER_RADIUS } from './hooks/useEraser';
import { useAnnotationKeyboard } from './hooks/useAnnotationKeyboard';
import { clamp } from './utils/geometry';
import { rasterizeAnnotation } from './utils/rasterize';
import { getAnnotationCursor, hitTestAnnotationUI, findShapeAtPoint } from './utils/hitTest';
const LS_TOOL_KEY = 'player.annotation.editor.tool';
const LS_COLOR_KEY = 'player.annotation.editor.color';
const VALID_TOOLS = new Set(['box', 'circle', 'line', 'arrow', 'freeform', 'text', 'eraser']);
function readSavedTool() {
    try {
        const val = localStorage.getItem(LS_TOOL_KEY);
        if (val && VALID_TOOLS.has(val))
            return val;
    }
    catch { /* ignore */ }
    return undefined;
}
const MIN_TEXT_WIDTH_NORM = 0.04;
function measureTextSize(text, fontSize, maxWidthPx, paddingPx = 8) {
    const div = document.createElement('div');
    div.style.cssText =
        'position:absolute;visibility:hidden;pointer-events:none;' +
            'display:inline-block;white-space:pre-wrap;word-break:break-word;' +
            'font-family:sans-serif;line-height:1.3;box-sizing:border-box;' +
            `padding:${paddingPx}px;max-width:${maxWidthPx}px;font-size:${fontSize}px;`;
    div.textContent = text;
    document.body.appendChild(div);
    // Add buffer to avoid sub-pixel rendering causing text clipping.
    // Height gets extra padding because line-height accumulation can push
    // the last line's descenders past the measured boundary.
    const width = Math.ceil(div.offsetWidth) + 2;
    const height = Math.ceil(div.scrollHeight) + 4;
    document.body.removeChild(div);
    return { width, height };
}
function readSavedColor() {
    try {
        const val = localStorage.getItem(LS_COLOR_KEY);
        if (val && /^#[0-9a-fA-F]{6}$/.test(val))
            return val;
    }
    catch { /* ignore */ }
    return undefined;
}
export const AnnotationEditor = forwardRef(function AnnotationEditor({ targetRef, initialShapes = [], isEditing, onSave, onDiscard, defaultColor, onMounted, toolbarPortalTarget: toolbarPortalTargetProp }, ref) {
    const [state, dispatch] = useReducer(annotationReducer, { initialShapes, defaultColor }, ({ initialShapes: shapes, defaultColor: color }) => createInitialAnnotationState(shapes, readSavedColor() ?? color, readSavedTool()));
    const [visible, setVisible] = useState(true);
    const [hoveredShapeId, setHoveredShapeId] = useState(null);
    const [cursorOverride, setCursorOverride] = useState(null);
    const [status, setStatus] = useState('');
    const containerSize = useContainerSize(targetRef);
    const textEditorRef = useRef(null);
    const canvasRef = useRef(null);
    // Track the text shape's height when editing starts so we never shrink below it.
    const textEditMinHeight = useRef(0);
    // Detect CSS scale from ancestor transforms (e.g. image viewer zoom).
    // The image viewer sets data-zoom on the annotation root when zoom changes.
    // We observe this attribute with a MutationObserver.
    const [zoom, setZoom] = useState(1);
    useEffect(() => {
        const el = targetRef.current;
        if (!el)
            return;
        const readZoom = () => {
            const z = parseFloat(el.dataset.zoom || '1') || 1;
            setZoom((prev) => Math.abs(prev - z) < 0.001 ? prev : z);
        };
        readZoom();
        if (typeof MutationObserver !== 'undefined') {
            const obs = new MutationObserver(readZoom);
            obs.observe(el, { attributes: true, attributeFilter: ['data-zoom'] });
            return () => obs.disconnect();
        }
    }, [targetRef]);
    // The annotation root's CSS aspect-ratio matches the video, so
    // containerSize already represents the video content area.
    const overlaySize = containerSize;
    // Actual screen dimensions from getBoundingClientRect. Unlike
    // offsetWidth (containerSize), this includes CSS transforms (e.g.
    // image viewer zoom via transform:scale). Used for handle sizing
    // in SelectionOutline so handles stay constant screen size
    // regardless of whether zoom is applied via container resize
    // (external mount) or CSS transform (in-canvas mount).
    const screenSize = useMemo(() => {
        // Recompute whenever container size or zoom changes
        const el = targetRef.current;
        if (!el)
            return overlaySize;
        const rect = el.getBoundingClientRect();
        return { width: rect.width || overlaySize.width, height: rect.height || overlaySize.height };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overlaySize.width, overlaySize.height, zoom]);
    // Generate a circular SVG cursor for the eraser tool sized to match
    // the eraser radius in pixel space.
    const eraserCursor = useMemo(() => {
        const dim = Math.min(overlaySize.width, overlaySize.height);
        if (dim <= 0)
            return 'crosshair';
        const diameterPx = Math.round(2 * ERASER_RADIUS * dim);
        const size = Math.max(diameterPx, 4) + 4; // room for strokes
        const half = size / 2;
        const r = (diameterPx / 2) || 2;
        // Subtle dual-ring cursor: dark outer, light inner, transparent center.
        // Low-contrast grays ensure visibility on any background without being harsh.
        const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'><circle cx='${half}' cy='${half}' r='${r + 1}' fill='none' stroke='rgba(0,0,0,0.5)' stroke-width='1'/><circle cx='${half}' cy='${half}' r='${r}' fill='none' stroke='rgba(255,255,255,0.7)' stroke-width='1'/></svg>`;
        return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${half} ${half}, crosshair`;
    }, [overlaySize.width, overlaySize.height]);
    // Persist tool and color to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(LS_TOOL_KEY, state.activeTool);
        }
        catch { /* ignore */ }
    }, [state.activeTool]);
    useEffect(() => {
        try {
            localStorage.setItem(LS_COLOR_KEY, state.activeColor);
        }
        catch { /* ignore */ }
    }, [state.activeColor]);
    // Capture the text shape's height when editing starts — used as a floor so
    // the box never shrinks below the user's drawn/resized size while typing.
    useLayoutEffect(() => {
        if (state.editingTextId) {
            const shape = state.shapes.find((s) => s.id === state.editingTextId);
            if (shape && shape.type === 'text')
                textEditMinHeight.current = shape.height;
        }
        else {
            textEditMinHeight.current = 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only reacting to editingTextId changes
    }, [state.editingTextId]);
    // Drawing hook (box, circle, arrow, freeform, text)
    const { onPointerDown: drawPointerDown, onPointerMove: drawPointerMove, onPointerUp: drawPointerUp, isDrawing, cancelDrawing, finishDrawing } = useDrawing({
        activeTool: state.activeTool,
        activeColor: state.activeColor,
        shapes: state.shapes,
        dispatch,
        containerWidth: overlaySize.width,
        containerHeight: overlaySize.height,
        zoom,
    });
    // Move hook (drag selected shapes)
    const { startMove, onMovePointer, endMove, nudge, isMoving } = useShapeMove({
        selectedIds: state.selectedIds,
        dispatch,
        containerWidth: overlaySize.width,
        containerHeight: overlaySize.height,
    });
    // Resize hook (drag corner/edge handles)
    const { startResize, onResizeMove, endResize, isResizing } = useShapeResize({
        shapes: state.shapes,
        dispatch,
        containerWidth: overlaySize.width,
        containerHeight: overlaySize.height,
    });
    // Rotate hook (drag rotation arcs)
    const { startRotate, onRotateMove, endRotate, isRotating } = useShapeRotate({
        shapes: state.shapes,
        dispatch,
        containerWidth: overlaySize.width,
        containerHeight: overlaySize.height,
    });
    // Eraser hook
    const { eraseAt } = useEraser({ shapes: state.shapes, dispatch });
    const erasingRef = useRef(false);
    // Keyboard shortcuts (Delete, Ctrl+Z/Y, arrows, tool hotkeys, Escape,
    // Tab cycle, Enter insert, [/] rotate, Ctrl+Shift+Arrow resize)
    useAnnotationKeyboard({
        dispatch,
        selectedIds: state.selectedIds,
        shapes: state.shapes,
        activeTool: state.activeTool,
        activeColor: state.activeColor,
        zoom,
        nudge,
        containerRef: targetRef,
        canvasRef,
        setStatus,
        startTextEdit: (id) => dispatch({ type: 'START_TEXT_EDIT', id }),
    });
    // Helper: get normalized SVG point from pointer event
    const getSvgPoint = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        return {
            x: clamp((e.clientX - rect.left) / rect.width, 0, 1),
            y: clamp((e.clientY - rect.top) / rect.height, 0, 1),
        };
    }, []);
    // --- Text editing ---
    const commitTextEdit = useCallback(() => {
        if (!state.editingTextId)
            return;
        const text = textEditorRef.current?.value ?? '';
        if (text.trim() === '') {
            // Empty text — delete the shape
            dispatch({ type: 'SELECT_SHAPE', id: state.editingTextId, additive: false });
            dispatch({ type: 'DELETE_SELECTED' });
        }
        else {
            // Update text + auto-resize width/height in a single dispatch (one undo step)
            const textShape = state.shapes.find((s) => s.id === state.editingTextId);
            if (textShape && overlaySize.width > 0 && overlaySize.height > 0) {
                const maxWidthPx = textShape.width * overlaySize.width;
                const measured = measureTextSize(text, textShape.fontSize * zoom, maxWidthPx, 8 * zoom);
                // Only auto-resize height (text wraps within current width).
                // Width is user-controlled via resize handles — never override it.
                // Never shrink below the height the shape had when editing started.
                const minH = textEditMinHeight.current || 0.02;
                const normH = Math.max(measured.height / overlaySize.height, minH);
                dispatch({
                    type: 'RESIZE_SHAPE',
                    id: state.editingTextId,
                    shape: { ...textShape, text, height: normH },
                });
            }
            else {
                dispatch({ type: 'UPDATE_TEXT', id: state.editingTextId, text });
            }
        }
        dispatch({ type: 'STOP_TEXT_EDIT' });
    }, [state.editingTextId, state.shapes, dispatch, overlaySize, zoom]);
    // Auto-expand text shape height as user types so all text is visible.
    const handleTextInput = useCallback(() => {
        const el = textEditorRef.current;
        if (!el || !state.editingTextId || overlaySize.width <= 0 || overlaySize.height <= 0)
            return;
        const textShape = state.shapes.find((s) => s.id === state.editingTextId);
        if (!textShape)
            return;
        const maxWidthPx = textShape.width * overlaySize.width;
        const measured = measureTextSize(el.value, textShape.fontSize * zoom, maxWidthPx, 8 * zoom);
        // Never shrink below the height the shape had when editing started.
        const minH = textEditMinHeight.current || 0.02;
        const normH = Math.max(measured.height / overlaySize.height, minH);
        // Only dispatch if height actually changed (avoids re-render loops)
        if (Math.abs(normH - textShape.height) > 0.001) {
            dispatch({ type: 'LIVE_RESIZE_TEXT', id: state.editingTextId, height: normH });
        }
    }, [state.editingTextId, state.shapes, overlaySize, dispatch, zoom]);
    // --- Unified pointer event handlers ---
    const handlePointerDown = useCallback((e) => {
        // If editing text, commit on click outside and stop — second click creates new box
        if (state.editingTextId) {
            commitTextEdit();
            return;
        }
        if (state.activeTool === 'eraser') {
            if (state.selectedIds.size > 0) {
                dispatch({ type: 'DESELECT_ALL' });
            }
            e.currentTarget.setPointerCapture(e.pointerId);
            erasingRef.current = true;
            const pt = getSvgPoint(e);
            eraseAt(pt.x, pt.y);
            return;
        }
        // JS-based hit detection — bypasses Firefox SVG hit testing bugs
        // with preserveAspectRatio="none". Uses coordinate math against
        // the SVG's actual rendered screen dimensions.
        const pt = getSvgPoint(e);
        const svgRect = e.currentTarget.getBoundingClientRect();
        const selectedShapes = state.shapes.filter((s) => state.selectedIds.has(s.id));
        // First check UI controls (handles, rotation dots, body) on selected shapes
        const uiHit = hitTestAnnotationUI(pt, selectedShapes, selectedShapes, svgRect.width, svgRect.height);
        if (uiHit) {
            e.currentTarget.setPointerCapture(e.pointerId);
            switch (uiHit.type) {
                case 'handle':
                    if (!state.selectedIds.has(uiHit.shapeId)) {
                        dispatch({ type: 'SELECT_SHAPE', id: uiHit.shapeId, additive: false });
                    }
                    startResize(uiHit.shapeId, uiHit.handle, e.clientX, e.clientY);
                    return;
                case 'rotate':
                    if (!state.selectedIds.has(uiHit.shapeId)) {
                        dispatch({ type: 'SELECT_SHAPE', id: uiHit.shapeId, additive: false });
                    }
                    startRotate(uiHit.shapeId, e);
                    return;
                case 'body':
                    if (!state.selectedIds.has(uiHit.shapeId)) {
                        dispatch({ type: 'SELECT_SHAPE', id: uiHit.shapeId, additive: false });
                    }
                    startMove(e.clientX, e.clientY);
                    return;
            }
        }
        // Check if pointer is on a shape stroke (click-to-select)
        const clickedShape = findShapeAtPoint(state.shapes, pt.x, pt.y);
        if (clickedShape) {
            // If text tool and clicked on a text shape, enter edit mode
            if (state.activeTool === 'text' && clickedShape.type === 'text') {
                dispatch({ type: 'SELECT_SHAPE', id: clickedShape.id, additive: false });
                dispatch({ type: 'START_TEXT_EDIT', id: clickedShape.id });
                return;
            }
            // Select the shape (click-to-select works regardless of active tool)
            const additive = e.shiftKey || e.ctrlKey || e.metaKey;
            dispatch({ type: 'SELECT_SHAPE', id: clickedShape.id, additive });
        }
        else {
            // Clicked empty space — deselect and start drawing immediately
            if (state.selectedIds.size > 0) {
                dispatch({ type: 'DESELECT_ALL' });
            }
            drawPointerDown(e);
        }
    }, [state.activeTool, state.selectedIds, state.editingTextId, state.shapes, eraseAt, drawPointerDown, getSvgPoint, dispatch, commitTextEdit, startResize, startRotate, startMove]);
    const handlePointerMove = useCallback((e) => {
        // Clamp coords to SVG bounds so shapes can't extend beyond video area
        const svgRect = e.currentTarget.getBoundingClientRect();
        const cx = clamp(e.clientX, svgRect.left, svgRect.right);
        const cy = clamp(e.clientY, svgRect.top, svgRect.bottom);
        if (isRotating()) {
            onRotateMove(cx, cy, e.shiftKey);
        }
        else if (isResizing()) {
            onResizeMove(cx, cy, e.shiftKey);
        }
        else if (isMoving()) {
            onMovePointer(cx, cy);
        }
        else if (state.activeTool === 'eraser' && erasingRef.current) {
            const pt = getSvgPoint(e);
            eraseAt(pt.x, pt.y);
        }
        else {
            drawPointerMove(e);
            // Update hover state and cursor when not actively drawing/moving/resizing/erasing.
            // Cursor is computed via JS coordinate math (not SVG element hover) to avoid
            // Firefox's buggy hit testing with preserveAspectRatio="none".
            // Uses the SVG's actual getBoundingClientRect dimensions so hit areas
            // match rendered positions exactly regardless of CSS transforms or zoom.
            if (state.activeTool !== 'eraser') {
                const pt = getSvgPoint(e);
                // Detect hovered shape via coordinate math (not DOM traversal)
                const hoveredShape = findShapeAtPoint(state.shapes, pt.x, pt.y);
                const id = hoveredShape?.id ?? null;
                setHoveredShapeId(id);
                const selectedShapes = state.shapes.filter((s) => state.selectedIds.has(s.id));
                const outlineIds = new Set(state.selectedIds);
                if (id)
                    outlineIds.add(id);
                const outlineShapes = state.shapes.filter((s) => outlineIds.has(s.id));
                const cursor = getAnnotationCursor(pt, selectedShapes, outlineShapes, svgRect.width, svgRect.height);
                setCursorOverride(cursor);
            }
        }
    }, [isRotating, isResizing, isMoving, onRotateMove, onResizeMove, onMovePointer, state.activeTool, state.shapes, state.selectedIds, eraseAt, drawPointerMove, getSvgPoint]);
    const handlePointerUp = useCallback((e) => {
        if (isRotating()) {
            endRotate();
        }
        else if (isResizing()) {
            endResize();
        }
        else if (isMoving()) {
            endMove();
        }
        else if (state.activeTool === 'eraser' && erasingRef.current) {
            erasingRef.current = false;
        }
        else {
            drawPointerUp(e);
        }
    }, [isRotating, isResizing, isMoving, endRotate, endResize, endMove, state.activeTool, drawPointerUp]);
    const handlePointerLeave = useCallback(() => {
        setHoveredShapeId(null);
        // Stop idle operations (drawing, erasing) when pointer leaves the
        // video area. But do NOT cancel moves, resizes, or rotations —
        // the pointer can legitimately leave the SVG during a drag (e.g.
        // when a text display div overlay repositions under the pointer).
        // setPointerCapture on the SVG ensures events keep flowing.
        if (isDrawing()) {
            finishDrawing();
        }
        else if (erasingRef.current) {
            erasingRef.current = false;
        }
    }, [isDrawing, finishDrawing]);
    // --- Toolbar callbacks ---
    const handleToolChange = useCallback((tool) => {
        if (state.editingTextId) {
            commitTextEdit();
        }
        dispatch({ type: 'SET_TOOL', tool });
        setStatus(`${tool} tool selected`);
    }, [dispatch, state.editingTextId, commitTextEdit]);
    const handleColorChange = useCallback((color) => {
        dispatch({ type: 'SET_COLOR', color });
        if (state.selectedIds.size > 0) {
            dispatch({ type: 'CHANGE_SELECTED_COLOR', color });
        }
    }, [dispatch, state.selectedIds.size]);
    const handleUndo = useCallback(() => {
        dispatch({ type: 'UNDO' });
        setStatus(`Undo successful`);
    }, [dispatch]);
    const handleRedo = useCallback(() => {
        dispatch({ type: 'REDO' });
        setStatus(`Redo successful`);
    }, [dispatch]);
    const handleDiscard = useCallback(() => {
        if (state.isDirty) {
            if (!window.confirm('You have unsaved annotation changes. Discard them?')) {
                return false;
            }
        }
        // Call onDiscard WITHOUT dispatching DISCARD first.
        // The onDiscard callback unmounts the component, so resetting state
        // is unnecessary. Dispatching before unmount triggers a React
        // re-render that interferes with the toolbar slide-out animation
        // (toolbar disappears, React re-renders and recreates it, then
        // the deferred cleanup removes it again).
        onDiscard?.();
        return true;
    }, [onDiscard, state.isDirty]);
    const handleSave = useCallback(() => {
        if (state.editingTextId) {
            commitTextEdit();
        }
        if (onSave) {
            onSave({ shapes: state.shapes });
            // Do NOT auto-mark as saved — the API consumer should call
            // markSaved() after confirming the save succeeded.
        }
    }, [onSave, state.shapes, state.editingTextId, commitTextEdit]);
    useImperativeHandle(ref, () => ({
        show: () => setVisible(true),
        discard: () => {
            dispatch({ type: 'DISCARD' });
            return true;
        },
        undo: () => dispatch({ type: 'UNDO' }),
        redo: () => dispatch({ type: 'REDO' }),
        getData: () => ({ shapes: state.shapes }),
        isDirty: () => state.isDirty,
        markSaved: () => dispatch({ type: 'MARK_SAVED' }),
        rasterize: (width, height) => rasterizeAnnotation(state.shapes, width, height),
    }), [state.shapes, state.isDirty]);
    // Fire onMounted callback once after the first render commit.
    useEffect(() => { onMounted?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Load initial shapes when they change
    useEffect(() => {
        if (initialShapes.length > 0) {
            dispatch({ type: 'LOAD_SHAPES', shapes: initialShapes });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    if (!visible || containerSize.width === 0)
        return null;
    // Collect shapes that should show outlines: selected + hovered (if not already selected)
    const outlineShapeIds = new Set(state.selectedIds);
    if (hoveredShapeId && !outlineShapeIds.has(hoveredShapeId) && state.activeTool !== 'eraser') {
        outlineShapeIds.add(hoveredShapeId);
    }
    // Find the text shape being edited
    const editingTextShape = state.editingTextId
        ? state.shapes.find((s) => s.id === state.editingTextId)
        : undefined;
    // Portal target: explicit prop if provided, otherwise the parent element
    // of the annotation root (the player wrapper for video/image viewers).
    const toolbarPortalTarget = toolbarPortalTargetProp ?? targetRef.current?.parentElement ?? null;
    return (_jsxs("div", { className: "hekate-annotation-editor", onDragStart: (e) => e.preventDefault(), children: [toolbarPortalTarget && createPortal(_jsx(Toolbar, { activeTool: state.activeTool, onToolChange: handleToolChange, onUndo: handleUndo, onRedo: handleRedo, canUndo: state.undoStack.length > 0, canRedo: state.redoStack.length > 0, onDiscard: handleDiscard, onSave: onSave ? handleSave : undefined, isEditing: !!isEditing, activeColor: state.activeColor, onColorChange: handleColorChange }), toolbarPortalTarget), _jsxs("div", { className: "hekate-annotation-overlay", style: {
                    cursor: state.activeTool === 'eraser'
                        ? eraserCursor
                        : cursorOverride || 'crosshair',
                }, children: [_jsx("div", { role: "status", "aria-live": "polite", className: "sr-only", children: status }), _jsx(SvgCanvas, { ref: canvasRef, shapes: state.shapes, drawingShape: state.drawingShape, isEditor: true, selectedIds: state.selectedIds, containerWidth: overlaySize.width, containerHeight: overlaySize.height, ariaLabel: "Annotation drawing canvas. Enter inserts a shape; Tab cycles through existing shapes.", onShapeFocus: (id) => dispatch({ type: 'SELECT_SHAPE', id, additive: false }), onShapeActivate: (id) => {
                            const shape = state.shapes.find((s) => s.id === id);
                            if (shape && shape.type === 'text') {
                                dispatch({ type: 'START_TEXT_EDIT', id });
                                setStatus('Editing text');
                            }
                        }, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerUp, onPointerLeave: handlePointerLeave, children: state.shapes
                            .filter((s) => outlineShapeIds.has(s.id))
                            .map((shape) => (_jsx(SelectionOutline, { shape: shape, containerWidth: screenSize.width, containerHeight: screenSize.height, showHandles: state.selectedIds.has(shape.id) }, `sel-${shape.id}`))) }), state.shapes
                        .filter((s) => s.type === 'text' && s.id !== state.editingTextId)
                        .map((shape) => (_jsx("div", { className: "hekate-annotation-text-display", draggable: false, style: {
                            position: 'absolute',
                            left: `${shape.x * 100}%`,
                            top: `${shape.y * 100}%`,
                            width: `${shape.width * 100}%`,
                            height: `${shape.height * 100}%`,
                            fontSize: `${shape.fontSize * zoom}px`,
                            color: shape.color,
                            fontFamily: 'sans-serif',
                            lineHeight: 1.3,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            padding: `${8 * zoom}px`,
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            pointerEvents: 'none',
                            transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
                            transformOrigin: 'center center',
                        }, children: shape.text }, `text-${shape.id}`))), editingTextShape && (_jsx("textarea", { ref: textEditorRef, className: "hekate-annotation-text-editor", style: {
                            position: 'absolute',
                            left: `${editingTextShape.x * 100}%`,
                            top: `${editingTextShape.y * 100}%`,
                            width: `${editingTextShape.width * 100}%`,
                            height: `${editingTextShape.height * 100}%`,
                            fontSize: `${editingTextShape.fontSize * zoom}px`,
                            color: editingTextShape.color,
                            fontFamily: 'sans-serif',
                            lineHeight: 1.3,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            padding: `${8 * zoom}px`,
                            boxSizing: 'border-box',
                            transform: editingTextShape.rotation ? `rotate(${editingTextShape.rotation}deg)` : undefined,
                            transformOrigin: 'center center',
                        }, defaultValue: editingTextShape.text, autoFocus: true, onInput: handleTextInput, onKeyDown: (e) => {
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                e.stopPropagation();
                                commitTextEdit();
                            }
                        } }))] })] }));
});
